import { addDoc, collection } from 'firebase/firestore'
import { createUniqueIdLong } from '../../../pure-js/libs/Common'
import { Collections } from '../../../pure-js/libs/Collections'
import {
  PortalLoginPerfLogEntry,
  PortalLoginPerfMilestone,
  PortalLoginPerfResult
} from '../../../pure-js/types/PortalLoginPerfLog'
import { db } from './Firebase'

/** TEMPORARY: disable or delete this module after portal login perf measurement window. */
export const PORTAL_LOGIN_PERF_ENABLED = true

const SESSION_KEY = 'portal_login_perf_flow'
const MAX_SPAN_MS = 300_000

type FlowState = {
  flowId: string
  routeFrom?: string
  marks: Record<string, number>
  spans: Record<string, number>
  retryCount: number
}

const loadFlow = (): FlowState | null => {
  if (!PORTAL_LOGIN_PERF_ENABLED) return null
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    return JSON.parse(raw) as FlowState
  } catch {
    return null
  }
}

const saveFlow = (state: FlowState): void => {
  if (!PORTAL_LOGIN_PERF_ENABLED) return
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state))
  } catch {
    // ignore quota / private mode errors
  }
}

export const clearPortalLoginPerfFlow = (): void => {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // ignore
  }
}

const clampSpan = (value: number): number => Math.max(0, Math.min(Math.round(value), MAX_SPAN_MS))

export const startPortalLoginPerfFlow = (routeFrom?: string): string | null => {
  if (!PORTAL_LOGIN_PERF_ENABLED) return null
  const flowId = createUniqueIdLong(16)
  const state: FlowState = {
    flowId,
    routeFrom,
    marks: {},
    spans: {},
    retryCount: 0
  }
  saveFlow(state)
  return flowId
}

export const markPortalLoginPerf = (name: string): void => {
  const flow = loadFlow()
  if (!flow) return
  flow.marks[name] = performance.now()
  saveFlow(flow)
}

export const setPortalLoginPerfSpan = (name: string, durationMs: number): void => {
  const flow = loadFlow()
  if (!flow) return
  flow.spans[name] = clampSpan(durationMs)
  saveFlow(flow)
}

export const measurePortalLoginPerfSpan = (spanName: string, startMark: string, endMark?: string): void => {
  const flow = loadFlow()
  if (!flow) return
  const start = flow.marks[startMark]
  if (start === undefined) return
  const end = endMark !== undefined ? flow.marks[endMark] : performance.now()
  flow.spans[spanName] = clampSpan(end - start)
  saveFlow(flow)
}

export const addPortalLoginPerfRetry = (): void => {
  const flow = loadFlow()
  if (!flow) return
  flow.retryCount += 1
  saveFlow(flow)
}

export const withPortalLoginPerfSpan = async <T>(spanName: string, fn: () => Promise<T>): Promise<T> => {
  const start = performance.now()
  try {
    return await fn()
  } finally {
    setPortalLoginPerfSpan(spanName, performance.now() - start)
  }
}

const getAppVersion = (): string => {
  const version = import.meta.env.VITE_APP_VERSION
  return typeof version === 'string' && version.length > 0 ? version : 'unknown'
}

const buildLogEntry = (
  milestone: PortalLoginPerfMilestone,
  result: PortalLoginPerfResult,
  flow: FlowState,
  extras?: Pick<PortalLoginPerfLogEntry, 'errorStage' | 'errorCode'>
): PortalLoginPerfLogEntry => {
  const now = new Date()
  const entry: PortalLoginPerfLogEntry = {
    createdAt: now.toISOString(),
    dateKey: now.toISOString().slice(0, 10),
    flowId: flow.flowId,
    milestone,
    result,
    appVersion: getAppVersion(),
    platform: 'web',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : 'unknown'
  }

  if (flow.routeFrom) {
    entry.routeFrom = flow.routeFrom.slice(0, 200)
  }

  const spanKeys: Array<keyof PortalLoginPerfLogEntry> = [
    'sendCodeToCodeScreenMs',
    'enterCodeToDashboardVisibleMs',
    'requestPortalLoginSMSMs',
    'sendVerificationCodeMs',
    'verifyCodeConfirmMs',
    'authStateResolveMs',
    'setPortalUserClaimsMs',
    'dashboardDataReadyMs'
  ]

  for (const key of spanKeys) {
    const value = flow.spans[key]
    if (value !== undefined) {
      ;(entry as Record<string, unknown>)[key] = value
    }
  }

  if (flow.retryCount > 0) {
    entry.retryCount = flow.retryCount
  }

  if (extras?.errorStage) {
    entry.errorStage = extras.errorStage.slice(0, 100)
  }
  if (extras?.errorCode) {
    entry.errorCode = extras.errorCode.slice(0, 100)
  }

  return entry
}

const writePerfLog = async (entry: PortalLoginPerfLogEntry): Promise<void> => {
  if (!PORTAL_LOGIN_PERF_ENABLED) return
  try {
    // Fire-and-forget perf log; no React state updates (temporary instrumentation).
    // eslint-disable-next-line database-safety/no-unsafe-firestore-operations
    await addDoc(collection(db, Collections.PORTAL_LOGIN_PERF_LOGS), entry)
  } catch (error) {
    console.warn('[PortalLoginPerf] Failed to write perf log', error)
  }
}

export const flushPortalLoginPerfSendCodeMilestone = async (): Promise<void> => {
  if (!loadFlow()) return
  measurePortalLoginPerfSpan('sendCodeToCodeScreenMs', 'sendCodeClick')
  const flow = loadFlow()
  if (!flow) return
  const entry = buildLogEntry(PortalLoginPerfMilestone.SendCodeToCodeScreen, PortalLoginPerfResult.Success, flow)
  await writePerfLog(entry)
}

export const flushPortalLoginPerfDashboardMilestone = async (): Promise<void> => {
  if (!loadFlow()) return
  measurePortalLoginPerfSpan('enterCodeToDashboardVisibleMs', 'enterCodeClick')
  const flow = loadFlow()
  if (!flow) return
  const entry = buildLogEntry(PortalLoginPerfMilestone.EnterCodeToDashboard, PortalLoginPerfResult.Success, flow)
  await writePerfLog(entry)
  clearPortalLoginPerfFlow()
}

export const flushPortalLoginPerfError = async (errorStage: string, errorCode?: string): Promise<void> => {
  const flow = loadFlow()
  if (!flow) return
  const milestone =
    flow.marks.enterCodeClick !== undefined
      ? PortalLoginPerfMilestone.EnterCodeToDashboard
      : PortalLoginPerfMilestone.SendCodeToCodeScreen
  const entry = buildLogEntry(milestone, PortalLoginPerfResult.Failed, flow, { errorStage, errorCode })
  await writePerfLog(entry)
  clearPortalLoginPerfFlow()
}
